import React, { useEffect, useRef } from 'react';
import Popover from '@mui/material/Popover';
import { Button, Box, Typography, IconButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'common/hook';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import { useTranslation } from 'react-i18next';
import { createStyleSheet } from 'utils/helpers';
import SyncIcon from '@mui/icons-material/Sync';
import {
    convertValueCustomerStatus,
    convertValueCustomSource,
} from '../constants';
import { customerFilterSchema } from 'common/schema';
import CustomerConstants from '../constants';
import {
    clearCustomerSearchForm,
    setCustomerSearchForm,
} from 'slices/customerSlice';
import { RootState } from 'plugins/store';

interface ICustomerFilterProps {
    open: boolean;
    headerFilterId: string | undefined;
    anchorEl: HTMLButtonElement | null;
    handleClose: () => void;
}

const CustomerFilter: React.FC<ICustomerFilterProps> = ({
    open,
    headerFilterId,
    anchorEl,
    handleClose,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const customersSearchForm = useAppSelector(
        (state: RootState) => state.customer.customersSearchForm
    );
    const methods = useForm({
        resolver: yupResolver(customerFilterSchema),
        defaultValues: {
            status: customersSearchForm.status || [
                CustomerConstants.status.ALL,
            ],
            source: customersSearchForm.source || [
                CustomerConstants.source.ALL,
            ],
            startDate: customersSearchForm.startDate || '',
            endDate: customersSearchForm.endDate || '',
        },
    });

    const { handleSubmit, reset, watch, setValue } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const params = { ...data };
        dispatch(setCustomerSearchForm(params));
        handleClose();

        return;
    });

    const handleRenewForm = () => {
        dispatch(clearCustomerSearchForm());
        reset({
            status: [CustomerConstants.status.ALL],
            source: [CustomerConstants.source.ALL],
            startDate: '',
            endDate: '',
        });
    };

    const topPixel = 104;

    const datePickerStyle = {
        '& .MuiInputBase-root': {
            height: '29px',
            borderRadius: '10px',
        },
        '& .MuiIconButton-root svg': {
            width: '14px',
            height: '14px',
        },
    };

    const checkBoxStyle = {
        '& .MuiFormControlLabel-label': {
            fontSize: '20px',
        },
    };

    const groupTitleStyle = {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '29px',
        marginBottom: '15px',
    };

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    React.useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setValue('endDate', '');
            }
        }
    }, [startDate, endDate, setValue]);

    const prevSourceRef = useRef<string[]>([]);

    useEffect(() => {
        const currentSource = watch('source') || [];
        const previousSource = prevSourceRef.current;

        const hasAll = currentSource.includes(CustomerConstants.source.ALL);
        const hadAll = previousSource.includes(CustomerConstants.source.ALL);

        const allIndividualSource = Object.values(
            CustomerConstants.source
        ).filter((source) => source !== CustomerConstants.source.ALL);
        const selectedWithoutAll = currentSource.filter(
            (source) => source !== CustomerConstants.source.ALL
        );

        if (!hadAll && hasAll) {
            setValue('source', [CustomerConstants.source.ALL]);
            prevSourceRef.current = [CustomerConstants.source.ALL];
            return;
        }

        if (hadAll && selectedWithoutAll.length > 0) {
            setValue('source', selectedWithoutAll);
            prevSourceRef.current = selectedWithoutAll;
            return;
        }

        if (
            !hasAll &&
            selectedWithoutAll.length === allIndividualSource.length
        ) {
            setValue('source', [CustomerConstants.source.ALL]);
            prevSourceRef.current = [CustomerConstants.source.ALL];
            return;
        }

        prevSourceRef.current = currentSource;
        // eslint-disable-next-line
    }, [watch('source')]);

    const prevStatusRef = useRef<string[]>([]);
    useEffect(() => {
        const currentStatus = watch('status') || [];
        const previousStatus = prevStatusRef.current;

        const hasAll = currentStatus.includes(CustomerConstants.status.ALL);
        const hadAll = previousStatus.includes(CustomerConstants.status.ALL);

        const allIndividualStatus = Object.values(
            CustomerConstants.status
        ).filter((status) => status !== CustomerConstants.status.ALL);
        const selectedWithoutAll = currentStatus.filter(
            (status) => status !== CustomerConstants.status.ALL
        );

        if (!hadAll && hasAll) {
            setValue('status', [CustomerConstants.status.ALL]);
            prevStatusRef.current = [CustomerConstants.status.ALL];
            return;
        }

        if (hadAll && selectedWithoutAll.length > 0) {
            setValue('status', selectedWithoutAll);
            prevStatusRef.current = selectedWithoutAll;
            return;
        }

        if (
            !hasAll &&
            selectedWithoutAll.length === allIndividualStatus.length
        ) {
            setValue('status', [CustomerConstants.status.ALL]);
            prevStatusRef.current = [CustomerConstants.status.ALL];
            return;
        }

        prevStatusRef.current = currentStatus;
        // eslint-disable-next-line
    }, [watch('status')]);
    return (
        <Popover
            id={headerFilterId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            sx={{
                '& .MuiPaper-root.MuiPopover-paper': {
                    top: `${topPixel}px !important`,
                    maxHeight: `min(906px, calc(100% - ${topPixel}px))`,
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        padding: '20px 30px 85px',
                        width: '430px',
                        overflowY: 'auto',
                        maxHeight: '700px',
                    }}
                >
                    <Box
                        mb={'20px'}
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Typography
                            sx={{
                                color: (theme) => theme.palette.primary.main,
                                fontWeight: 800,
                                fontSize: '27px',
                                lineHeight: '33px',
                            }}
                        >
                            {t('layout.header.filter.title')}
                        </Typography>
                        <IconButton
                            onClick={handleRenewForm}
                            aria-label="refresh"
                        >
                            <SyncIcon
                                sx={{
                                    fontSize: 32,
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thời gian
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            gap={1}
                        >
                            {t('layout.header.filter.time.start')}
                            <BaseDatePicker
                                name="startDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                            {t('layout.header.filter.time.end')}
                            <BaseDatePicker
                                name="endDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </Box>
                    </Box>

                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Trạng thái
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="status"
                                options={
                                    Object.keys(CustomerConstants.status).map(
                                        (key) => {
                                            const type =
                                                CustomerConstants.status[
                                                    key as keyof typeof CustomerConstants.status
                                                ];
                                            return {
                                                label: convertValueCustomerStatus(
                                                    type
                                                ),
                                                value: key.toLowerCase(),
                                            };
                                        }
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Nguồn
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="source"
                                // call api to get source
                                options={
                                    Object.keys(CustomerConstants.source).map(
                                        (key) => {
                                            const type =
                                                CustomerConstants.source[
                                                    key as keyof typeof CustomerConstants.source
                                                ];
                                            return {
                                                label: convertValueCustomSource(
                                                    type
                                                ),
                                                value: key.toLowerCase(),
                                            };
                                        }
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    display={'flex'}
                    justifyContent={'end'}
                    gap={2}
                    padding={'20px 30px'}
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        style={styles.modalButton}
                    >
                        Đóng
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        aria-label="filter"
                        onClick={onSubmit}
                        style={styles.modalButton}
                    >
                        Lọc
                    </Button>
                </Box>
            </FormProvider>
        </Popover>
    );
};

const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '40px',
        minHeight: '42px',
        minWidth: '84px',
        fontSize: '14px',
        fontWeight: '600',
        borderWidth: '2px',
    },
});

export default CustomerFilter;
